import { array, boolean, number, object, string } from 'yup';

import { formatDateForInput, formatForWire } from 'utilities/common';

import { MAX_LENGTH_ERROR_MESSAGE, NULLABLE_STRING, REQUIRED_NUMBER } from 'modules/common/constants';
import { ZERO_GUID } from 'modules/enumerations/enumerationIds';

// EnumerationV2 lookups use the zero GUID for "nothing selected"; the form uses '' so a Select shows None
const toSelectValue = (guid) => (guid && guid !== ZERO_GUID ? guid : '');

const assignmentLengthSchema = number().nullable()
  .transform((value, originalValue) => {
    if (typeof originalValue !== 'string') return value;
    const numericValue = originalValue.replace(/\s+days$/i, '').replace(/,/g, '').trim();
    return numericValue === '' ? null : Number(numericValue);
  })
  .integer('Assignment length must be a whole number.');

// One-to-one copy of the Visa/Immigration card on the authorization's Global service, keyed by intake contact
export class GlobalVisaInformation {
  static schema = object().shape({
    intakeContactId: REQUIRED_NUMBER,
    visaStatusId: NULLABLE_STRING,
    currentVisaId: NULLABLE_STRING,
    visaExpiredDate: NULLABLE_STRING,
    visaMaxDate: NULLABLE_STRING,
    visaIssuedById: NULLABLE_STRING,
    visaDocumentNumber: string().max(255, MAX_LENGTH_ERROR_MESSAGE),
    visaInterviewDate: NULLABLE_STRING,
    visaConsulate: string().max(255, MAX_LENGTH_ERROR_MESSAGE),
    visaReceiptNumber: string().max(255, MAX_LENGTH_ERROR_MESSAGE),
    visaTravelToDate: NULLABLE_STRING,
    passportNumber: string().max(255, MAX_LENGTH_ERROR_MESSAGE),
    passportReceivedDate: NULLABLE_STRING,
    passportExpiredDate: NULLABLE_STRING,
  });

  constructor(wireData) {
    const json = wireData || {};
    this.id = json.id;
    this.intakeContactId = json.intakeContactId;
    this.visaStatusId = toSelectValue(json.visaStatusId);
    this.currentVisaId = toSelectValue(json.currentVisaId);
    this.visaExpiredDate = formatDateForInput(json.visaExpiredDate);
    this.visaMaxDate = formatDateForInput(json.visaMaxDate);
    this.visaIssuedById = toSelectValue(json.visaIssuedById);
    this.visaDocumentNumber = json.visaDocumentNumber || '';
    this.visaInterviewDate = formatDateForInput(json.visaInterviewDate);
    this.visaConsulate = json.visaConsulate || '';
    this.visaReceiptNumber = json.visaReceiptNumber || '';
    this.visaTravelToDate = formatDateForInput(json.visaTravelToDate);
    this.passportNumber = json.passportNumber || '';
    this.passportReceivedDate = formatDateForInput(json.passportReceivedDate);
    this.passportExpiredDate = formatDateForInput(json.passportExpiredDate);
  }

  toWireData() {
    return formatForWire({
      id: this.id,
      intakeContactId: this.intakeContactId,
      visaStatusId: this.visaStatusId,
      currentVisaId: this.currentVisaId,
      visaExpiredDate: this.visaExpiredDate,
      visaMaxDate: this.visaMaxDate,
      visaIssuedById: this.visaIssuedById,
      visaDocumentNumber: this.visaDocumentNumber,
      visaInterviewDate: this.visaInterviewDate,
      visaConsulate: this.visaConsulate,
      visaReceiptNumber: this.visaReceiptNumber,
      visaTravelToDate: this.visaTravelToDate,
      passportNumber: this.passportNumber,
      passportReceivedDate: this.passportReceivedDate,
      passportExpiredDate: this.passportExpiredDate,
    });
  }
}

//  of the intake form (international moves only). visaContacts drives the family member selector and
// visaInfos holds one visa record per selected contact; syncVisaInfos keeps the two aligned.
export default class GlobalServiceInformation {
  static schema = object().shape({
    immigrationServiceProviderId: NULLABLE_STRING,
    homeCountryId: NULLABLE_STRING,
    homeStateId: NULLABLE_STRING,
    citizenshipId: NULLABLE_STRING,
    payInCurrencyId: NULLABLE_STRING,
    isEligibleForTaxService: boolean(),
    certOfCoverageExp: NULLABLE_STRING,
    assignmentLength: assignmentLengthSchema,
    visaContacts: array().of(object().shape({
      id: REQUIRED_NUMBER,
    })),
    visaInfos: array().of(GlobalVisaInformation.schema),
    destinationServices: array().of(object().shape({ intakeContactId: REQUIRED_NUMBER })),
    languageTraining: array().of(object().shape({ intakeContactId: REQUIRED_NUMBER })),
    internationalSchooling: array().of(object().shape({ intakeContactId: REQUIRED_NUMBER })),
  });

  constructor(wireData, contacts = []) {
    const json = wireData || {};
    this.immigrationServiceProviderId = toSelectValue(json.immigrationServiceProviderId);
    this.homeCountryId = toSelectValue(json.homeCountryId);
    this.homeStateId = toSelectValue(json.homeStateId);
    this.citizenshipId = toSelectValue(json.citizenshipId);
    this.payInCurrencyId = toSelectValue(json.payInCurrencyId);
    this.isEligibleForTaxService = json.isEligibleForTaxService === true;
    this.certOfCoverageExp = formatDateForInput(json.certOfCoverageExp);
    const assignmentLength = assignmentLengthSchema.validateSync(json.assignmentLength == null ? null : json.assignmentLength);
    this.assignmentLength = assignmentLength == null ? '' : `${assignmentLength} Days`;
    // Drop rows whose family member no longer exists on the form
    this.visaInfos = (json.visaInfos || [])
      .filter((visaInfo) => contacts.some((contact) => contact.id === visaInfo.intakeContactId))
      .map((visaInfo) => new GlobalVisaInformation(visaInfo));
    this.visaContacts = contacts.filter((contact) => this.visaInfos.some((visaInfo) => visaInfo.intakeContactId === contact.id));
    this.destinationServices = (json.destinationServices || []).filter((row) => contacts.some((contact) => contact.id === row.intakeContactId));
    this.languageTraining = (json.languageTraining || []).filter((row) => contacts.some((contact) => contact.id === row.intakeContactId));
    this.internationalSchooling = (json.internationalSchooling || []).filter((row) => contacts.some((contact) => contact.id === row.intakeContactId));
  }

  static fromWireData(wireData, contacts) {
    return new GlobalServiceInformation(wireData, contacts);
  }

  static generateErrors(errors) {
    return errors;
  }

  // Returns one visa record per selected contact, keeping any record the contact already had
  static syncVisaInfos(visaInfos, selectedContacts) {
    return selectedContacts.map((contact) =>
      visaInfos.find((visaInfo) => visaInfo.intakeContactId === contact.id) || new GlobalVisaInformation({ intakeContactId: contact.id }),
    );
  }

  toWireData(contacts) {
    const selectedContactIds = this.visaContacts
      .filter((selected) => !!selected.id && contacts.some((contact) => contact.id === selected.id))
      .map((selected) => selected.id);

    return formatForWire({
      immigrationServiceProviderId: this.immigrationServiceProviderId,
      homeCountryId: this.homeCountryId,
      homeProvinceId: this.homeProvinceId,
      citizenshipId: this.citizenshipId,
      payInCurrencyId: this.payInCurrencyId,
      isEligibleForTaxService: this.isEligibleForTaxService,
      certificateOfCoverageExpiration: this.certificateOfCoverageExpiration,
      globalEstStartDate: this.globalEstStartDate,
      globalEstEndDate: this.globalEstEndDate,
      assignmentLength: assignmentLengthSchema.validateSync(this.assignmentLength),
      destinationServices: this.destinationServices.filter((row) => contacts.some((contact) => !!contact.id && contact.id === row.intakeContactId)),
      languageTraining: this.languageTraining.filter((row) => contacts.some((contact) => !!contact.id && contact.id === row.intakeContactId)),
      internationalSchooling: this.internationalSchooling.filter((row) => contacts.some((contact) => !!contact.id && contact.id === row.intakeContactId)),
      visaInfos: this.visaInfos
        .filter((visaInfo) => selectedContactIds.includes(visaInfo.intakeContactId))
        .map((visaInfo) => visaInfo.toWireData()),
    });
  }
}
